import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Check, MessageCircle, Facebook } from "lucide-react";
import { toast } from "sonner";

/**
 * Share button with QR code + copy-link + quick share to WhatsApp / Facebook.
 */
export default function ShareButton({ itemId, title }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const resolvedTitle = title || t("share.default_title");
  const shareUrl = `${window.location.origin}/galerie#${itemId}`;
  const msg = t("share.wa_msg", { title: resolvedTitle });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("share.toast_copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("share.toast_copy_failed"));
    }
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${msg} ${shareUrl}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/70 hover:bg-orange-500 border border-white/20 text-white text-xs font-bold rounded backdrop-blur transition"
        data-testid={`share-${itemId}`}
      >
        <Share2 size={12} />
        {t("share.btn")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md bg-zinc-950 border-white/10 text-white"
          data-testid="share-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-outfit font-bold">
              {t("share.dialog_title")}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col items-center gap-4 py-2">
            <div className="p-4 bg-white rounded-xl" data-testid="share-qr">
              <QRCodeSVG
                value={shareUrl}
                size={180}
                level="M"
                includeMargin={false}
              />
            </div>
            <div className="text-xs text-zinc-400 text-center">
              {t("share.scan_qr")}
            </div>

            <div className="w-full">
              <div className="flex items-center gap-2 bg-zinc-900 border border-white/10 rounded-md p-2">
                <div
                  className="flex-1 text-xs text-zinc-300 truncate"
                  data-testid="share-url"
                >
                  {shareUrl}
                </div>
                <Button
                  size="sm"
                  onClick={copy}
                  className={`h-8 px-3 ${
                    copied
                      ? "bg-emerald-500 hover:bg-emerald-600"
                      : "bg-orange-500 hover:bg-orange-600"
                  } text-white`}
                  data-testid="share-copy-btn"
                >
                  {copied ? (
                    <>
                      <Check size={12} className="mr-1" /> {t("share.copied_label")}
                    </>
                  ) : (
                    <>
                      <Copy size={12} className="mr-1" /> {t("share.copy_label")}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="w-full grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={shareWhatsApp}
                className="border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 hover:text-emerald-300"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle size={14} className="mr-2" /> WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={shareFacebook}
                className="border-blue-500/40 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
                data-testid="share-facebook-btn"
              >
                <Facebook size={14} className="mr-2" /> Facebook
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
